import React from "react";
import FormWrapper from "../shared/FormWrapper";
import ProgressBar from "../shared/ProgressBar";
import AutoSaveIndicator from "../shared/AutoSaveIndicator";

const denominationOptions = [
  { value: "anglican", label: "Anglican / Church of England" },
  { value: "baptist", label: "Baptist" },
  { value: "catholic", label: "Catholic" },
  { value: "charismatic", label: "Charismatic" },
  { value: "evangelical", label: "Evangelical" },
  { value: "methodist", label: "Methodist" },
  { value: "pentecostal", label: "Pentecostal" },
  { value: "presbyterian", label: "Presbyterian" },
  { value: "reformed", label: "Reformed" },
  { value: "non-denominational", label: "Non-denominational" },
  { value: "other", label: "Other" },
];

export default function ChurchAffiliation({
  formData,
  onInputChange,
  onContinue,
  onBack,
  onSaveAndExit,
  saving,
  lastSaved,
}) {
  const isAffiliated = formData.churchAffiliation === "affiliated";

  return (
    <FormWrapper>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "8px 12px",
          fontSize: "14px",
          fontWeight: "500",
          color: "#737373",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "color 0.2s ease",
          marginBottom: "8px",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#1a1a1a")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#737373")}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      {/* Progress Bar */}
      <ProgressBar currentStep={3} totalSteps={5} />

      {/* Icon and Title */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "32px",
          animation: "fadeInUp 0.5s ease",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            backgroundColor: "#f0fdf4",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            border: "1px solid #d1fae5",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 21h18" />
            <path d="M5 21V7l7-4 7 4v14" />
            <path d="M9 21v-4h6v4" />
          </svg>
        </div>

        <h1
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#1a1a1a",
            marginBottom: "8px",
            letterSpacing: "-0.5px",
          }}
        >
          Church connection
        </h1>
        <p
          style={{
            fontSize: "15px",
            color: "#737373",
            margin: 0,
          }}
        >
          Are you connected to a local church?
        </p>
      </div>

      {/* Affiliation Toggle */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "32px",
          animation: "fadeInUp 0.5s ease 0.1s both",
        }}
      >
        <button
          onClick={() => onInputChange("churchAffiliation", "affiliated")}
          style={{
            flex: 1,
            padding: "16px 12px",
            fontSize: "14px",
            fontWeight: "600",
            color: isAffiliated ? "#fff" : "#525252",
            backgroundColor: isAffiliated ? "#1a1a1a" : "#fff",
            border: isAffiliated ? "1px solid #1a1a1a" : "1px solid #e5e5e5",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
          onMouseEnter={(e) => {
            if (!isAffiliated) {
              e.currentTarget.style.borderColor = "#1a1a1a";
            }
          }}
          onMouseLeave={(e) => {
            if (!isAffiliated) {
              e.currentTarget.style.borderColor = "#e5e5e5";
            }
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 21h18" />
            <path d="M5 21V7l7-4 7 4v14" />
            <path d="M9 21v-4h6v4" />
          </svg>
          Yes, I'm part of a church
        </button>
        <button
          onClick={() => onInputChange("churchAffiliation", "independent")}
          style={{
            flex: 1,
            padding: "16px 12px",
            fontSize: "14px",
            fontWeight: "600",
            color: !isAffiliated ? "#fff" : "#525252",
            backgroundColor: !isAffiliated ? "#1a1a1a" : "#fff",
            border: !isAffiliated ? "1px solid #1a1a1a" : "1px solid #e5e5e5",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
          onMouseEnter={(e) => {
            if (isAffiliated) {
              e.currentTarget.style.borderColor = "#1a1a1a";
            }
          }}
          onMouseLeave={(e) => {
            if (isAffiliated) {
              e.currentTarget.style.borderColor = "#e5e5e5";
            }
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          Independent / Freelance
        </button>
      </div>

      {/* Church Details (only show if affiliated) */}
      {isAffiliated && (
        <div
          style={{
            animation: "fadeInUp 0.3s ease",
          }}
        >
          {/* Church Name */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#1a1a1a",
                marginBottom: "8px",
              }}
            >
              Church name
            </label>
            <input
              type="text"
              value={formData.churchName}
              onChange={(e) => onInputChange("churchName", e.target.value)}
              placeholder="Which church are you part of?"
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "16px",
                color: "#1a1a1a",
                backgroundColor: "#fff",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                outline: "none",
                transition: "all 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#1a1a1a";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Church City */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#1a1a1a",
                marginBottom: "8px",
              }}
            >
              Church location
            </label>
            <input
              type="text"
              value={formData.churchCity}
              onChange={(e) => onInputChange("churchCity", e.target.value)}
              placeholder="City or town"
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "16px",
                color: "#1a1a1a",
                backgroundColor: "#fff",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                outline: "none",
                transition: "all 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#1a1a1a";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Denomination */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#1a1a1a",
                marginBottom: "8px",
              }}
            >
              Denomination
            </label>
            <select
              value={formData.denomination}
              onChange={(e) => onInputChange("denomination", e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "16px",
                color: formData.denomination ? "#1a1a1a" : "#a3a3a3",
                backgroundColor: "#fff",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                outline: "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#1a1a1a";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <option value="">Select denomination...</option>
              {denominationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Role at Church */}
          <div style={{ marginBottom: "32px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#1a1a1a",
                marginBottom: "8px",
              }}
            >
              Your role
            </label>
            <input
              type="text"
              value={formData.churchRole}
              onChange={(e) => onInputChange("churchRole", e.target.value)}
              placeholder="E.g., Pastor, Worship Leader, Elder, Member..."
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "16px",
                color: "#1a1a1a",
                backgroundColor: "#fff",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                outline: "none",
                transition: "all 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#1a1a1a";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <p style={{ fontSize: "13px", color: "#a3a3a3", marginTop: "8px" }}>
              This helps churches understand your background
            </p>
          </div>
        </div>
      )}

      {/* Independent message */}
      {!isAffiliated && (
        <div
          style={{
            padding: "20px",
            backgroundColor: "#f0fdf4",
            borderRadius: "8px",
            marginBottom: "32px",
            textAlign: "center",
            border: "1px solid #d1fae5",
            animation: "fadeInUp 0.3s ease",
          }}
        >
          <p style={{ fontSize: "14px", color: "#047857", margin: 0, fontWeight: "500" }}>
            No problem! You can still serve churches as an independent minister
            or freelancer.
          </p>
        </div>
      )}

      {/* Auto-save indicator */}
      <AutoSaveIndicator saving={saving} lastSaved={lastSaved} />

      {/* Continue Button - Dark style */}
      <button
        onClick={onContinue}
        style={{
          width: "100%",
          padding: "16px 24px",
          fontSize: "16px",
          fontWeight: "600",
          color: "#fff",
          backgroundColor: "#1a1a1a",
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#333";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#1a1a1a";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        Continue
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>

      {/* Save & Exit - subtle link below */}
      <button
        onClick={onSaveAndExit}
        style={{
          width: "100%",
          padding: "12px",
          marginTop: "12px",
          fontSize: "14px",
          fontWeight: "500",
          color: "#a3a3a3",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "color 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#737373")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#a3a3a3")}
      >
        Save & finish later
      </button>
    </FormWrapper>
  );
}
